/**
 * SMS Conversations — owner-facing dashboard for AI-handled SMS threads.
 * Two-pane layout: conversation list (left) + selected timeline + reply box (right).
 *
 * Owner can:
 *   - See every AI exchange with leads in real time (auto-poll every 8s)
 *   - Pause AI for a specific thread when the conversation gets nuanced
 *   - Step in with a manual reply (still goes through the same SignalWire pool)
 *   - Mark a thread as read
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  MessageSquare, Send, Bot, User, Loader2, RefreshCw, Pause, Play, ChevronLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 8000;

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const m = Math.floor((Date.now() - d.getTime()) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m`;
    if (m < 60 * 24) return `${Math.floor(m / 60)}h`;
    if (m < 60 * 24 * 7) return `${Math.floor(m / 60 / 24)}d`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

function timeFull(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString([], {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso.slice(0, 16); }
}

export default function SMSConversationsPage() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);  // selected phone string
  const [thread, setThread] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  // ── Load conversation list ──
  const loadList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sms/conversations`, { headers });
      setConversations(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load conversations');
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  // ── Load selected thread ──
  const loadThread = useCallback(async (phone, opts = {}) => {
    if (!phone) return;
    if (!opts.silent) setLoadingThread(true);
    try {
      const { data } = await axios.get(`${API}/sms/conversations/${encodeURIComponent(phone)}`, { headers });
      setThread(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load thread');
    } finally {
      if (!opts.silent) setLoadingThread(false);
    }
  }, [token]);

  // Initial load + polling
  useEffect(() => {
    loadList();
    const id = setInterval(loadList, POLL_MS);
    return () => clearInterval(id);
  }, [loadList]);

  useEffect(() => {
    if (!selected) return;
    loadThread(selected);
    const id = setInterval(() => loadThread(selected, { silent: true }), POLL_MS);
    return () => clearInterval(id);
  }, [selected, loadThread]);

  // Mark read on open
  useEffect(() => {
    if (!selected) return;
    axios.post(`${API}/sms/conversations/${encodeURIComponent(selected)}/mark-read`, {}, { headers })
      .then(() => loadList())
      .catch(() => { /* non-critical */ });
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to newest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread?.messages?.length]);

  const sendReply = async () => {
    const body = reply.trim();
    if (!body || sending) return;
    setSending(true);
    setError('');
    try {
      await axios.post(
        `${API}/sms/conversations/${encodeURIComponent(selected)}/reply`,
        { body },
        { headers },
      );
      setReply('');
      await loadThread(selected, { silent: true });
      await loadList();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const toggleAI = async () => {
    if (!thread) return;
    try {
      await axios.post(
        `${API}/sms/conversations/${encodeURIComponent(selected)}/ai-toggle`,
        { paused: !thread.ai_paused },
        { headers },
      );
      await loadThread(selected, { silent: true });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to toggle AI');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="sms-conversations-page">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <header className="mb-5 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
              <MessageSquare size={22} className="text-primary" /> SMS Conversations
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Every AI text exchange with your leads. Step in any time.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { loadList(); if (selected) loadThread(selected); }}
            data-testid="sms-refresh-btn"
          >
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
        </header>

        {error && (
          <div
            className="mb-3 px-3 py-2 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm"
            data-testid="sms-error"
          >
            {error}
            <button onClick={() => setError('')} className="ml-3 underline">dismiss</button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 h-[calc(100vh-180px)]">
          {/* ── LEFT: conversation list ── */}
          <aside
            className={`bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden flex flex-col ${selected ? 'hidden lg:flex' : ''}`}
            data-testid="sms-conversation-list"
          >
            <div className="p-3 border-b border-slate-800 text-xs font-bold uppercase tracking-wider text-slate-400">
              {loadingList ? 'Loading…' : `${conversations.length} ${conversations.length === 1 ? 'thread' : 'threads'}`}
            </div>
            <div className="flex-1 overflow-y-auto">
              {!loadingList && conversations.length === 0 && (
                <div className="p-6 text-center text-slate-500 text-sm">
                  No SMS conversations yet. They'll show up here as soon as a lead texts your number.
                </div>
              )}
              {conversations.map((c) => (
                <button
                  key={c.phone}
                  onClick={() => setSelected(c.phone)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors ${selected === c.phone ? 'bg-slate-800/60' : ''}`}
                  data-testid={`sms-conv-${c.phone}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-100 truncate">
                        {c.customer_name || c.phone}
                      </div>
                      <div className="text-[11px] text-slate-500">{c.phone}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-[10px] text-slate-500">{timeAgo(c.last_at)}</span>
                      {c.unread > 0 && (
                        <span className="text-[10px] font-bold bg-primary/30 text-primary px-1.5 rounded-full">
                          {c.unread}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-1">
                    {c.last_direction === 'outbound' && <span className="text-slate-500">You: </span>}
                    {c.last_body}
                  </p>
                  {c.ai_paused && (
                    <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold text-amber-400">
                      <Pause size={10} /> AI paused
                    </span>
                  )}
                </button>
              ))}
            </div>
          </aside>

          {/* ── RIGHT: selected thread ── */}
          <main
            className={`bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden flex flex-col ${!selected ? 'hidden lg:flex' : 'flex'}`}
            data-testid="sms-thread-pane"
          >
            {!selected && (
              <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
                Select a conversation to read the AI's exchange.
              </div>
            )}

            {selected && (
              <>
                {/* Thread header */}
                <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                  <button
                    onClick={() => setSelected(null)}
                    className="lg:hidden text-slate-400 hover:text-slate-200"
                    data-testid="sms-back-btn"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-slate-100 truncate">
                      {thread?.customer_name || selected}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {selected} {thread?.industry && `· ${thread.industry}`}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleAI}
                    className={thread?.ai_paused ? 'text-emerald-400' : 'text-amber-400'}
                    data-testid="sms-ai-toggle-btn"
                  >
                    {thread?.ai_paused
                      ? <><Play size={12} className="mr-1" /> Resume AI</>
                      : <><Pause size={12} className="mr-1" /> Pause AI</>}
                  </Button>
                </div>

                {/* Pause banner */}
                {thread?.ai_paused && (
                  <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-amber-300 text-xs flex items-center gap-2">
                    <Pause size={12} /> AI auto-reply is paused for this thread. Resume any time — or keep replying manually below.
                  </div>
                )}

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="sms-message-list">
                  {loadingThread && !thread && (
                    <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
                      <Loader2 size={16} className="animate-spin mr-2" /> Loading…
                    </div>
                  )}
                  {(thread?.messages || []).map((m) => {
                    const me = m.direction === 'outbound';
                    const ai = me && (m.trigger_type === 'ai_auto_reply' || m.auto_reply);
                    return (
                      <div
                        key={m.id}
                        className={`flex ${me ? 'justify-end' : 'justify-start'}`}
                        data-testid={`sms-msg-${m.id}`}
                      >
                        <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                          me
                            ? (ai ? 'bg-violet-500/15 border border-violet-500/30 text-violet-100' : 'bg-primary/15 border border-primary/30 text-primary-foreground')
                            : 'bg-slate-800/60 border border-slate-700/40 text-slate-100'
                        }`}>
                          <div className="flex items-center gap-1.5 mb-0.5 text-[10px] uppercase tracking-wider opacity-70">
                            {me ? (ai ? <><Bot size={10} /> AI</> : <><User size={10} /> You</>) : <><User size={10} /> Lead</>}
                            <span>· {timeFull(m.created_at)}</span>
                            {m.status && m.status !== 'sent' && m.status !== 'delivered' && (
                              <span className="text-amber-400">· {m.status}</span>
                            )}
                          </div>
                          <p className="whitespace-pre-wrap break-words">{m.body}</p>
                        </div>
                      </div>
                    );
                  })}
                  {thread && thread.messages.length === 0 && (
                    <div className="text-center text-slate-500 text-sm py-8">No messages yet.</div>
                  )}
                </div>

                {/* Reply box */}
                <div className="border-t border-slate-800 p-3">
                  <div className="flex items-end gap-2">
                    <textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendReply();
                      }}
                      placeholder={thread?.ai_paused ? 'AI is paused — your reply will be the next message.' : 'Type to override the AI…'}
                      rows={2}
                      className="flex-1 resize-none px-3 py-2 rounded-md bg-slate-950 border border-slate-700 text-sm text-slate-100 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                      data-testid="sms-reply-input"
                    />
                    <Button
                      onClick={sendReply}
                      disabled={!reply.trim() || sending}
                      data-testid="sms-reply-send-btn"
                    >
                      {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </Button>
                  </div>
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    Press ⌘/Ctrl+Enter to send. Manual replies always go through — even if AI is on.
                  </p>
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
